/**
 * Application Identity Configuration
 *
 * Single source of truth for the project name and branding.
 * Change these values here and they propagate everywhere.
 *
 * Usage:
 *   import { APP } from '../config/app';
 *   console.log(APP.name);          // 'ComDesBook'
 *   console.log(APP.storageKey('user')); // 'comdesbook_user'
 */

// ── Core Identity ──
const APP_NAME = 'ComDesBook';
const APP_SLUG = 'comdesbook';           // lowercase, no spaces — used for storage keys, CSS, URLs
const ORG_NAME = 'ComDes';              // short org/department name
const ORG_FULL = 'Communication Design'; // full department name
const EMAIL_DOMAIN = 'comdes.ie';        // email domain for demo/test accounts
const BASE_PATH = '';                     // root deployment (Cloudflare Pages with custom domain)

export const APP = Object.freeze({
  // Display names
  name: APP_NAME,                              // 'ComDesBook'
  fullName: `${ORG_FULL} Equipment Booking`,   // 'Communication Design Equipment Booking'
  shortName: ORG_NAME,                         // 'ComDes'
  orgFull: ORG_FULL,                           // 'Communication Design'
  description: `Book equipment and spaces at ${ORG_FULL}`,

  // Technical identifiers
  slug: APP_SLUG,                              // 'comdesbook'
  basePath: BASE_PATH,                         // '/ComDesBook'
  emailDomain: EMAIL_DOMAIN,                   // 'comdes.ie'

  // Helper: generate consistent localStorage keys
  // e.g. APP.storageKey('user') → 'comdesbook_user'
  storageKey: (suffix) => `${APP_SLUG}_${suffix}`,

  // Helper: generate demo email addresses
  // e.g. APP.email('admin') → 'admin@comdes.ie'
  email: (user) => `${user}@${EMAIL_DOMAIN}`,

  // Helper: build absolute path from basePath
  // e.g. APP.path('/student') → '/ComDesBook/student'
  path: (route = '/') => `${BASE_PATH}${route === '/' ? '' : route}`,

  // Production domain check (for disabling demo mode)
  isProduction: (hostname) => hostname.includes('cdbooking.com') || hostname.includes('comdes.com') || hostname.includes('comdes.ie'),

  // Report/export branding
  reports: {
    title: `${ORG_FULL} Equipment Booking System`,
    subtitle: `${APP_NAME} Analytics`,
    filePrefix: `${ORG_NAME}_`,            // e.g. 'ComDes_Analytics_Report_2024.pdf'
    equipmentOffice: `${ORG_FULL} Equipment Office`,
  },
});

// ── Support contact ──
//
// Shown on /support, which is the App Store Support URL. A Cloudflare Email Routing
// rule on cdbooking.com forwards it to a monitored NCAD staff mailbox (set up
// 2026-09-19). Change the forwarding there, not the address here.
export const SUPPORT_EMAIL = 'support@cdbooking.com';

// ── Storage Key Constants (for direct imports) ──
// These are the most commonly used; import individually when you
// don't need the full APP object.
export const STORAGE_KEYS = Object.freeze({
  USER: APP.storageKey('user'),
  TOKEN: APP.storageKey('token'),
  LOGGED_OUT: APP.storageKey('logged_out'),
  BOOKING_CART: APP.storageKey('booking_cart'),
  RECENT_SEARCHES: APP.storageKey('recent_searches'),
  // v2 — the admin dashboards moved to fixed rows with one span per row, so a
  // layout stored against the old free-span widget set is deliberately ignored
  // rather than migrated. Bump this again if the row structure changes.
  DASHBOARD_LAYOUT: APP.storageKey('dashboard_layout_v2'),
});

export default APP;
